import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { toast } from 'react-hot-toast';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'agent';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, role: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkSession = () => {
      try {
        // Check for demo user in localStorage
        const demoUser = localStorage.getItem('demo_user');
        if (demoUser) {
          setUser(JSON.parse(demoUser));
        }
      } catch (error) {
        console.error('Session check error:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      // Demo login - check against predefined accounts
      const demoAccounts = [
        { email: 'admin@demo.com', password: 'admin123', name: 'Demo Admin', role: 'super_admin' as const },
        { email: 'agent@demo.com', password: 'agent123', name: 'Demo Agent', role: 'agent' as const }
      ];

      const demoAccount = demoAccounts.find(acc => acc.email === email && acc.password === password);
      
      if (demoAccount) {
        // Demo login successful - store in localStorage for persistence
        const userData = {
          id: 'demo-' + demoAccount.role,
          email: demoAccount.email,
          name: demoAccount.name,
          role: demoAccount.role
        };
        
        localStorage.setItem('demo_user', JSON.stringify(userData));
        setUser(userData);
        toast.success('Login successful!');
        return;
      }

      throw new Error('Invalid credentials');
    } catch (error: any) {
      toast.error('Invalid email or password');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (name: string, email: string, password: string, role: string) => {
    setLoading(true);
    try {
      // For demo purposes, just show success message
      toast.success('Account created successfully! Please use the demo accounts to login.');
    } catch (error: any) {
      toast.error('Registration failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      localStorage.removeItem('demo_user');
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error: any) {
      toast.error('Logout failed');
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 